import React, { FC, ReactElement } from 'react';
import TouchableElement from '@components/common/touchableElement';
import { Text, View } from 'react-native';
import Svg from '@styles/icons/svg';
import { scale } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import { checkColor, WithColor } from '@styles/colors';
import { font } from '@styles/fonts';
import { ICON_BUTTON_PADDING } from '@v2/pages/common/constants/base.constants';
import { isWeb, SCREEN_PADDING_HORIZONTAL } from '@helpers';
import { ImageDocumentStoreItem, RetrievedDocumentReferenceLink } from '@v2/components/imageHandling/types/types';
import { useManageAttachButton } from './hooks/useManageAttachButton';
import { useManageAttachments } from './hooks/useManageAttachments';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import AttachModal from '@v2/components/imageHandling/components/Header/components/AttachModal';
import RemoveAttachmentModal from '@v2/components/imageHandling/components/Header/components/RemoveAttachmentModal';
import { useSelector } from 'react-redux';
import { selectCurrentContextImagesDocuments } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';

export interface AttachButtonProps extends WithColor {
  bg?: string;
  shouldUseRelativeLeftPosition?: boolean;
  leftRelativeOffset?: number;
  topRelativeOffset?: number;
  renderCustomButton?: (onPress: () => void) => ReactElement;
  images?: ImageDocumentStoreItem[];
  onNavigate?: () => void;
  selectedIndex?: number;
}

const AttachButton: FC<AttachButtonProps> = ({
  C,
  renderCustomButton,
  shouldUseRelativeLeftPosition,
  images: imagesFromProps,
  topRelativeOffset = 35,
  leftRelativeOffset = 0,
  bg = 'Button/Light/Only Text',
  onNavigate,
  selectedIndex,
}) => {
  const imagesForCurrentContext = useSelector(selectCurrentContextImagesDocuments);
  const images = imagesFromProps || imagesForCurrentContext;
  const { amountOfLinks, attachButtonMeasurements, attachButtonRef, selectedImage } = useManageAttachButton({ images, selectedIndex });

  const {
    handleConfirmRemoveAttachment,
    handleStartRemovingAttachment,
    handleToggleFavoriteLink,
    onClose,
    setShowAttachModal,
    setShowRemoveAttachmentModal,
    showAttachModal,
    showRemoveAttachmentModal,
  } = useManageAttachments({ img: selectedImage });

  const onAttachButtonPress = () => setShowAttachModal(true);

  return (
    <View ref={attachButtonRef}>
      {renderCustomButton && renderCustomButton(() => onAttachButtonPress())}
      {!renderCustomButton && (
        <TouchableElement testID={TEST_IDS.documentsHeader.attachButton} onPress={() => onAttachButtonPress()}>
          {({ boxStyle, pressHoverColor }) => {
            const activeBg = showAttachModal ? checkColor(bg, 'pressed') : pressHoverColor(bg);
            return (
            <View
              style={[
                boxStyle,
                { position: 'relative', backgroundColor: C(activeBg), padding: scale(ICON_BUTTON_PADDING), borderRadius: scale(18) },
                isWeb && { outline: 'none' },
              ]}
            >
              <Svg icon="clip" width={scale(19.5)} height={scale(22)} color={C('Link/Light/Primary')} />
              <Text
                style={[
                  font['Small'],
                  {
                    position: 'absolute',
                    left: scale(26),
                    top: scale(18),
                    color: C('Link/Light/Primary'),
                    pointerEvents: 'none',
                  },
                ]}
              >
                {amountOfLinks ?? 0}
              </Text>
            </View>
            );
          }}
        </TouchableElement>
      )}

      {selectedImage && (
        <AttachModal
          key="attachModal"
          visible={showAttachModal}
          onClose={onClose}
          onNavigate={onNavigate}
          links={(selectedImage?.links || []) as RetrievedDocumentReferenceLink[]}
          onToggleFavorite={handleToggleFavoriteLink}
          onRemoveAttachment={handleStartRemovingAttachment}
          imageId={selectedImage?.id}
          fileName={selectedImage?.fileName}
          img={selectedImage}
          styles={{
            left: shouldUseRelativeLeftPosition ? attachButtonMeasurements.x + leftRelativeOffset : SCREEN_PADDING_HORIZONTAL,
            top: attachButtonMeasurements.y + scale(topRelativeOffset),
          }}
        />
      )}

      <RemoveAttachmentModal
        visible={showRemoveAttachmentModal}
        onClose={() => setShowRemoveAttachmentModal(false)}
        onConfirm={handleConfirmRemoveAttachment}
      />
    </View>
  );
};

export default AttachButton;
